import { AfterViewInit, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { list } from 'src/app/common/classes/request-builder';
import { createCsv } from '@fitogest/reactive-form-builder/csv';
import { ColInterface } from 'src/app/common/components/common-list-table/col.interface';
import { ColStylesInterface } from 'src/app/common/components/common-list-table/col-styles.interface';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { Utils } from 'src/app/common/utils';
import { RequestHandler } from 'src/app/service/OffService/request-handler';
import { TareasApiService } from 'src/app/service/api/tareas-api.service';
import { DashboardService } from 'src/app/service/events/dashboard.service';
import { Filtering } from 'src/app/service/filtering/filtering';
import { BaseView } from 'src/app/view/base-view';
import { environment } from 'src/environments/environment';
import { TreeNode } from 'primeng/api';
import { FormType } from '@fitogest/reactive-form-builder/interfaces';
import { SubcontratoModel } from 'src/app/models/tareas/subcontrato.model';
import { SubcontratosFormDefinition } from './subcontratos-form/form-control/form-definition';
import { getDropdownSelectedSingle, setDropdownSelected } from '@fitogest/reactive-form-builder/utils';

const TRUTHY_VALUES = new Set<unknown>([1, '1', true, 'true']);

@Component({
    selector: 'app-subcontratos',
    standalone: false,
    templateUrl: './subcontratos.component.html',
    styleUrls: ['./subcontratos.component.css']
})
export class SubcontratosComponent extends BaseView<SubcontratoModel> implements OnInit, AfterViewInit {
    public form: SubcontratosFormDefinition | null = null;
    public rolUser = StorageManager.getUser().rol;
    public hiddenButtonDelete = false;

    public appName = environment.appName;
    public fincaName = environment.features.fincaName;
    public hasClients = environment.features.hasClients;
    public serverUrl = environment.serverUrl;
    public userCanSeeClientes = this.hasClients &&
        (environment.features.userCanSeeClientes ? true : StorageManager.getUser().rol === 'admin');

    public getRequest: RequestHandler<SubcontratoModel[]> = this.tareasApi.subcontratos.GET;
    public deleteRequest: RequestHandler<SubcontratoModel> = this.tareasApi.subcontratos.DELETE;
    public pathToForm = 'crear-subcontratos';
    public menuItemId = 'crear-subcontratos';

    public override filtering: Filtering<SubcontratoModel> = new Filtering<SubcontratoModel>();
    public override canShow: Record<string, boolean> = {};
    public override model: SubcontratoModel = {};

    public prioridad = list();
    public fecha: { inicio: string; fin: string } = {
        inicio: '',
        fin: '',
    };

    public costeTotal = 0;

    public cols: ColInterface[] = [
        { field: 'finca', header: this.fincaName, filter: '' },
        { field: 'fecha', header: 'Fecha', filter: '' },
        { field: 'proveedor', header: 'Proveedor', filter: '' },
        { field: 'concepto', header: 'Concepto', filter: '' },
        { field: 'coste', header: 'Coste', filter: '' }
    ];

    constructor(
        public tareasApi: TareasApiService,
        private dashboard: DashboardService,
        private router: Router,
    ) {
        super(dashboard, tareasApi.subcontratos.GET, tareasApi.subcontratos.DELETE);
    }

    public get selectedPrioridad(): string {
        const selected = getDropdownSelectedSingle(this.prioridad);

        return typeof selected === 'string' ? selected : '';
    }

    public ngOnInit(): void {
        if (environment.features.showTareasMto) {
            if (this.rolUser !== 'admin') {
                this.hiddenButtonDelete = true;
            }

            setDropdownSelected(this.prioridad, 'No-Realizada');
        }

        this.dashboard.setSubmenuItem('tareas', this.menuItemId);
        this.addSubcontratosFilters();
    }

    public ngAfterViewInit(): void {
        this.tareasApi.subcontratos.GET.response(() => this.updateFooter());
    }

    public add(): void {
        this.router.navigate(['dashboard', this.pathToForm]);
    }

    public edit(data: SubcontratoModel): void {
        this.router.navigate(['dashboard', 'editar-subcontratos', data.id]);
    }

    public duplicate(data: SubcontratoModel): void {
        this.router.navigate(['dashboard', 'duplicar-subcontratos', data.id]);
    }

    public override show(modal: string, data: SubcontratoModel): void {
        super.show(modal, data);

        const formType = modal === 'delete' ? +FormType.DELETION : +FormType.INFORMATIVE;
        this.form = new SubcontratosFormDefinition(formType, data);
    }

    public filtrarRealizados(value: string, from = 'prioridad'): void {
        if (from === 'prioridad') {
            setDropdownSelected(this.prioridad, this.selectedPrioridad === value ? '' : value);
        }

        this.filtering.filter();
        this.updateFooter();
    }

    public downloadCsv(): void {
        this.form = new SubcontratosFormDefinition(+FormType.EXPORT, {});
        const rows = this.getFilteredRows();
        const csvContent = createCsv(this.form.fieldNames, this.form.fieldLabels, rows);
        const csvLink = document.getElementById('csvLink') as HTMLAnchorElement | null;

        if (csvLink) {
            csvLink.download = 'Subcontratos.csv';
            csvLink.href = encodeURI(csvContent);
            csvLink.click();
        }
    }

    public override sum(field: string): void {
        const sum = this.getFilteredRows()
            .reduce((acc, row) => acc + this.toFiniteNumber(row[field]), 0);
        const roundedSum = Math.round(sum * 100) / 100;

        this.cols.forEach((col: ColInterface) => {
            if (col.field === field) {
                col.footer = roundedSum.toString();
            }
        });

        this.costeTotal = roundedSum;
    }

    public moneyFormat(input: string | number | undefined): string {
        return Utils.decimalFormat(input ?? 0, 2);
    }

    public override updateFooter(): void {
        this.sum('coste');
    }

    public globalConditionalStyle(_value: string, col: ColInterface): ColStylesInterface {
        if (col.field === 'prioridad') {
            return {
                textAlign: 'center',
            };
        }

        return {
            textAlign: 'center'
        };
    }

    public globalVisualTransform(value: string, col: ColInterface): string {
        const fieldsSiNo = ['limpieza_calles', 'bolsas_basura', 'limpieza_desagues'];

        if (fieldsSiNo.includes(col.field)) {
            return TRUTHY_VALUES.has(value) ? 'Sí' : 'No';
        }

        return value;
    }

    public getFechaInicio(event: string): void {
        this.fecha.inicio = event;
        this.filtering.filter();
        this.updateFooter();
    }

    public getFechaFin(event: string): void {
        this.fecha.fin = event;
        this.filtering.filter();
        this.updateFooter();
    }

    private addSubcontratosFilters(): void {
        this.filtering.addFilter((copy: TreeNode<SubcontratoModel>[] | void) =>
            (copy as TreeNode<SubcontratoModel>[]).filter((row) => {
                const subcontrato = this.getRowData(row);

                switch (this.selectedPrioridad) {
                case 'Realizada':
                    return ['Realizada'].includes(subcontrato.prioridad ?? '');
                case 'No-Realizada':
                    return !['Realizada'].includes(subcontrato.prioridad ?? '');
                default:
                    return true;
                }
            }));

        if (StorageManager.getUser().rol !== 'admin') {
            this.filtering.addFilter((copy: TreeNode<SubcontratoModel>[] | void) =>
                (copy as TreeNode<SubcontratoModel>[]).filter((row) => {
                    const subcontrato = this.getRowData(row);

                    if (subcontrato.ids_trabajadores != null) {
                        const idTrabajador = StorageManager.getUser().id;
                        const listaTrabajadores = subcontrato.ids_trabajadores.toString().split(';');

                        return listaTrabajadores.includes(idTrabajador?.toString() ?? '');
                    }

                    return false;
                }));
        }

        this.filtering.addFilter((copy: TreeNode<SubcontratoModel>[] | void) =>
            (copy as TreeNode<SubcontratoModel>[]).filter((row) => {
                const subcontrato = this.getRowData(row);

                if (!this.fecha.inicio || !this.fecha.fin || !subcontrato.fecha) {
                    return true;
                }

                const arrayFecha = subcontrato.fecha.toString().split('/');
                const fechaAux = `${arrayFecha[2]}-${arrayFecha[1]}-${arrayFecha[0]}`;

                return moment(moment(this.fecha.inicio).format('YYYY-MM-DD')).diff(fechaAux) <= 0 &&
                    moment(moment(this.fecha.fin).format('YYYY-MM-DD')).diff(fechaAux) >= 0;
            }));
    }

    private getFilteredRows(): SubcontratoModel[] {
        return ((this.filtering.value ?? []) as Array<TreeNode<SubcontratoModel> | SubcontratoModel>)
            .map((row) => this.getRowData(row))
            .filter((row): row is SubcontratoModel => row !== null);
    }

    private getRowData(row: TreeNode<SubcontratoModel> | SubcontratoModel | null | undefined): SubcontratoModel {
        if (row && typeof row === 'object' && 'data' in row && row.data) {
            return row.data as SubcontratoModel;
        }

        return (row ?? {}) as SubcontratoModel;
    }

    private toFiniteNumber(value: unknown): number {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : 0;
        }

        if (typeof value === 'string') {
            const normalized = value.trim().replace(/\./g, '').replace(',', '.');
            const parsed = Number(normalized);

            return Number.isFinite(parsed) ? parsed : 0;
        }

        return 0;
    }
}
